// src/pods/podStageComposition.ts
var ROLE_SPEED = {
  ps08x: 0.8,
  ps: 1,
  ps15x: 1.5,
  ps2x: 2,
  trans: 1
};
var isTargetRole = (role) => role !== "trans";
var _warnedTrailingKnownStages = /* @__PURE__ */ new Set();
function buildMainStage(sentence, stage, sentenceIdx, playlist, uniformSpeed) {
  const useUniform = typeof uniformSpeed === "number" && Number.isFinite(uniformSpeed) && uniformSpeed > 0;
  const speedFor = (role) => useUniform ? uniformSpeed : ROLE_SPEED[role] ?? 1;
  const sentencePlays = [];
  for (let j = 0; j < playlist.length; j++) {
    let playRole = playlist[j];
    if (playRole === "explainer") {
      if (playlist.includes("trans")) continue;
      playRole = "trans";
    }
    if (playRole === "trans" && !sentence.known_audio_id) continue;
    const isTrans = playRole === "trans";
    const audioId = isTrans ? sentence.known_audio_id : sentence.target_audio_id;
    sentencePlays.push({
      sentenceIdx,
      stage,
      playRole,
      audioId,
      text: isTrans ? sentence.known_text : sentence.target_text,
      playbackSpeed: speedFor(playRole),
      speedIsFinal: useUniform,
      glueToNextChunk: false
      // set on the ACTUAL last play below
    });
  }
  if (sentencePlays.length === 0) return [];
  const last = sentencePlays[sentencePlays.length - 1];
  if (!isTargetRole(last.playRole)) {
    const lastTarget = [...sentencePlays].reverse().find((p) => isTargetRole(p.playRole));
    const closeRole = lastTarget?.playRole ?? "ps";
    sentencePlays.push({
      sentenceIdx,
      stage,
      playRole: closeRole,
      audioId: sentence.target_audio_id,
      text: sentence.target_text,
      playbackSpeed: speedFor(closeRole),
      speedIsFinal: useUniform,
      glueToNextChunk: false
    });
    if (!_warnedTrailingKnownStages.has(stage)) {
      _warnedTrailingKnownStages.add(stage);
      console.warn(
        `[podScheduler] Stage ${stage} playlist [${playlist.join(", ")}] ends on a non-target slot \u2014 learner would be stranded on the known language. Appended a target rep as a safety net; fix the saved stagePlaylist so it ends on a ps* slot.`
      );
    }
  }
  sentencePlays[sentencePlays.length - 1].glueToNextChunk = !!sentence.glue_to_next;
  return sentencePlays;
}

// src/pods/fusionDrill.ts
var DRILL_SLICE_PLAYBACK_ENABLED = false;
var normalizeForAudio = (t) => (t || "").toLowerCase().trim().replace(/\s+/g, " ").replace(/[.!。！]+$/, "");
var normalizeForLookup = (t) => (t || "").normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{M}\p{N}]+/gu, " ").trim();
var lookupKeyVariants = (t) => {
  const base = (t || "").toLowerCase().trim().replace(/\s+/g, " ").replace(/[\u2018\u2019]/g, "'");
  if (!base) return [];
  const bare = base.replace(/[.!?…。！？؟¿¡\s]+$/u, "");
  return Array.from(/* @__PURE__ */ new Set([base, bare, `${bare}.`, `${bare}?`, `${bare}!`]));
};
var voiceFamily = (id) => (id || "").replace(/^(xai|azure|cartesia)_/, "");
var POD_KNOWN_VOICE_FAMILY = "gfzdpspr5fdp";
var POD_KNOWN_FEMALE_VOICE_FAMILY = "bedd6226";
var otherPodVoice = (voiceId) => {
  const v = voiceFamily(voiceId);
  if (v === POD_KNOWN_VOICE_FAMILY) return POD_KNOWN_FEMALE_VOICE_FAMILY;
  if (v === POD_KNOWN_FEMALE_VOICE_FAMILY) return POD_KNOWN_VOICE_FAMILY;
  return null;
};
function resolveKnownWithFallback(voice, inVoice) {
  const own = inVoice(voice);
  if (own) return own;
  if (own === void 0) return null;
  const other = otherPodVoice(voice);
  return other && inVoice(other) || null;
}
function knownClipsForText(text, lookup) {
  const whole = text ? lookup(text) : null;
  if (whole) return [whole];
  const parts = (text || "").split(/(?<=[.!?])\s+/).map((t) => t.trim()).filter(Boolean);
  if (parts.length < 2) return [];
  const ids = parts.map((t) => lookup(t));
  return ids.every(Boolean) ? ids : [];
}
var fineKnownKey = (voiceId, normText) => `${voiceFamily(voiceId)}|${normText}`;
var dominantVoiceFamily = (voiceIds) => {
  const n = /* @__PURE__ */ new Map();
  for (const v of voiceIds) if (v) n.set(voiceFamily(v), (n.get(voiceFamily(v)) || 0) + 1);
  let best = null;
  for (const [k, c] of n) if (c > (best ? n.get(best) : 0)) best = k;
  return best;
};
var SENTENCE_PUNCT = /[.!?…。！？؟]/;
function fuseSpans(spans, mode) {
  if (spans.length <= 1) return spans;
  const out = [];
  if (mode === "chained") {
    for (let i = 0; i < spans.length - 1; i++) out.push({ start: spans[i].start, end: spans[i + 1].end });
  } else {
    for (let i = 0; i < spans.length; i += 2)
      out.push({ start: spans[i].start, end: spans[Math.min(i + 1, spans.length - 1)].end });
  }
  return out;
}
function spanLadder(n, mode) {
  let spans = Array.from({ length: n }, (_, i) => ({ start: i, end: i }));
  const levels = [spans];
  while (spans.length > 1) {
    spans = fuseSpans(spans, mode);
    levels.push(spans);
  }
  return levels;
}
function partitionUnits(turnText, units) {
  const text = turnText || "";
  const lower = text.toLowerCase();
  const groups = [[]];
  let cursor = 0;
  for (let i = 0; i < units.length; i++) {
    const surface = (units[i].target_surface || "").toLowerCase();
    const idx = surface ? lower.indexOf(surface, cursor) : -1;
    if (i > 0 && idx !== -1 && SENTENCE_PUNCT.test(text.slice(cursor, idx))) groups.push([]);
    groups[groups.length - 1].push(i);
    if (idx !== -1) cursor = idx + surface.length;
  }
  return groups.filter((g) => g.length > 0);
}
var wordCount = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;
var isBareInterjection = (unit) => wordCount(unit?.target_surface) <= 1;
function glueLeadingInterjection(groups, units) {
  if (groups.length >= 2 && groups[0].length === 1 && (!units || isBareInterjection(units[groups[0][0]]))) {
    return [[...groups[0], ...groups[1]], ...groups.slice(2)];
  }
  return groups;
}
function buildFusionGroups(input, opts) {
  const units = (input.fineMap || []).filter(
    (u) => u && (u.kind === void 0 || u.kind === "atom" || u.kind === "passthrough") && u.target_surface
  );
  if (units.length === 0) return null;
  const raw = partitionUnits(input.turnTargetText, units);
  const glued = glueLeadingInterjection(raw, units);
  const rows = input.rows || [];
  const singleRow = rows.length === 1;
  if (!singleRow && raw.length !== rows.length) return null;
  const takeg = input.takegAudioIds;
  if (takeg && takeg.length !== glued.length) return null;
  const gluedOffset = glued.length === raw.length ? 0 : 1;
  const glueHasNoTake = gluedOffset === 1 && !singleRow && !(takeg && takeg[0]);
  const splitGlued = (!!opts?.splitGlued || glueHasNoTake) && gluedOffset === 1;
  const groupsSrc = splitGlued ? raw : glued;
  const takegIndexFor = (gi) => splitGlued ? Math.max(0, gi - 1) : gi;
  const flatStarts = [];
  {
    let off = 0;
    for (const g of groupsSrc) {
      flatStarts.push(off);
      off += g.length;
    }
  }
  const windowIndex = {};
  for (const w of input.windowKnownMap || []) windowIndex[`${w.start}-${w.end}`] = w.known;
  const groups = groupsSrc.map((unitIdxs, gi) => {
    const groupUnits = unitIdxs.map((i) => units[i]);
    const rowFirst = singleRow ? 0 : splitGlued ? gi : gi === 0 ? 0 : gi + gluedOffset;
    const rowLast = singleRow ? 0 : splitGlued ? gi : gi === 0 ? gluedOffset : gi + gluedOffset;
    const rowsOfGroup = rows.slice(rowFirst, rowLast + 1);
    const perSentenceRow = !singleRow && rowsOfGroup.length === 1 ? rowsOfGroup[0] : null;
    const wholeTurnGroup = singleRow && groupsSrc.length === 1 ? rows[0] : null;
    const coversTurn = groupsSrc.length === 1;
    const takegId = takeg ? takeg[takegIndexFor(gi)] || null : null;
    const sliceable = DRILL_SLICE_PLAYBACK_ENABLED;
    return {
      groupIndex: gi,
      units: groupUnits,
      windowKnowns: windowIndex,
      flatStart: flatStarts[gi],
      takegAudioId: takegId,
      wholeTargetClipId: perSentenceRow?.targetAudioId || wholeTurnGroup?.targetAudioId || takegId || (coversTurn ? input.turnTargetAudioId || null : null),
      wholeKnownClipId: perSentenceRow?.knownAudioId || wholeTurnGroup?.knownAudioId || (coversTurn ? input.turnKnownAudioId || null : null),
      wholeKnownClipIds: rowsOfGroup.length > 1 && rowsOfGroup.every((r) => r.knownAudioId) ? rowsOfGroup.map((r) => r.knownAudioId) : [],
      targetText: perSentenceRow?.targetText || wholeTurnGroup?.targetText || (rowsOfGroup.length > 1 ? rowsOfGroup.map((r) => r.targetText).filter(Boolean).join(" ") : groupUnits.map((u) => u.target_surface).join(" ")),
      knownText: perSentenceRow?.knownText || wholeTurnGroup?.knownText || (rowsOfGroup.length > 1 ? rowsOfGroup.map((r) => r.knownText).filter(Boolean).join(" ") : groupUnits.map((u) => u.gloss || "").filter(Boolean).join(" ")),
      rowFirst,
      rowLast,
      sliceable
    };
  });
  if (groups.some((g) => !g.wholeTargetClipId)) return null;
  return groups;
}
var SKIP_FINEST_RUNG = false;
function ladderFor(group, mode) {
  if (!group.sliceable) return [[{ start: 0, end: group.units.length - 1 }]];
  let levels = spanLadder(group.units.length, mode);
  return levels;
}
function groupDepth(group, mode) {
  return ladderFor(group, mode).length;
}
var FUSION_TIER_PREFIX = "fusion-r";
function buildFusionRungPlays(sentence, group, rung, sentenceIdx, mode, lookupKnownClip) {
  const { steps } = rungStepsForGroup(group, rung, mode, lookupKnownClip);
  const plays = [];
  for (const st of steps) {
    if (!st.clip) continue;
    plays.push({
      sentenceIdx,
      stage: 0,
      tier: `${FUSION_TIER_PREFIX}${rung}`,
      playRole: st.kind === "known" ? "trans" : "ps",
      audioId: st.clip.id,
      text: st.text,
      playbackSpeed: 1,
      glueToNextChunk: false,
      ...st.clip.startMs != null && st.clip.endMs != null ? { startMs: st.clip.startMs, endMs: st.clip.endMs } : {}
    });
  }
  if (plays.length > 0 && sentence.glue_to_next) plays[plays.length - 1].glueToNextChunk = true;
  return plays;
}
function rungStepsForGroup(group, rung, mode, lookupKnownClip, stripOffset = 0) {
  const ladder = ladderFor(group, mode);
  const level = ladder[Math.min(rung, ladder.length - 1)];
  const strips = [];
  const steps = [];
  const pushTktt = (stripIndex, chunkText, chunkClip, knownText, knownClips) => {
    steps.push({ kind: "chunk", stripIndex, text: chunkText, clip: chunkClip });
    for (const clip of knownClips) steps.push({ kind: "known", stripIndex, text: knownText, clip });
    steps.push({ kind: "chunk", stripIndex, text: chunkText, clip: chunkClip });
    steps.push({ kind: "chunk", stripIndex, text: chunkText, clip: chunkClip });
  };
  if (level.length === 1) {
    const sentText = group.knownText;
    const glossJoin = group.units.map((u) => u.gloss || "").filter(Boolean).join(" ");
    const knownText = sentText || glossJoin;
    const clipId = (sentText ? lookupKnownClip(sentText) : null) || group.wholeKnownClipId || (glossJoin ? lookupKnownClip(glossJoin) : null);
    const knownClips = (clipId ? [clipId] : group.wholeKnownClipIds.length ? group.wholeKnownClipIds : knownClipsForText(knownText, lookupKnownClip)).map((id) => ({ id }));
    strips.push({ target: group.targetText, known: knownText });
    pushTktt(
      stripOffset,
      group.targetText,
      group.wholeTargetClipId ? { id: group.wholeTargetClipId } : null,
      knownText,
      knownClips
    );
    return { strips, steps };
  }
  level.forEach((span, si) => {
    const us = group.units.slice(span.start, span.end + 1);
    const chunkText = us.map((u) => u.target_surface).join(" ");
    const chunkClip = {
      id: group.takegAudioId,
      startMs: us[0].target_start_ms,
      endMs: us[us.length - 1].target_end_ms
    };
    const knownText = us.length === 1 ? us[0].gloss || "" : group.windowKnowns[`${group.flatStart + span.start}-${group.flatStart + span.end}`] || us.map((u) => u.gloss || "").filter(Boolean).join(" ");
    const knownId = knownText ? lookupKnownClip(knownText) : null;
    strips.push({ target: chunkText, known: knownText });
    pushTktt(stripOffset + si, chunkText, chunkClip, knownText, knownId ? [{ id: knownId }] : []);
  });
  return { strips, steps };
}

// src/pods/podTurns.ts
function computeTurnSpans(sentences) {
  const spans = [];
  let start = 0;
  for (let i = 0; i < sentences.length; i++) {
    if (!sentences[i].glue_to_next || i === sentences.length - 1) {
      spans.push({ start, end: i });
      start = i + 1;
    }
  }
  return spans;
}
function turnSpanForIndex(spans, index) {
  return spans.find((s) => index >= s.start && index <= s.end) ?? null;
}
function podPlayShowsTurnText(play) {
  return !!play && !play.isLayer1;
}
function podLapPlayedSentenceIndices(plays) {
  const set = /* @__PURE__ */ new Set();
  for (const p of plays) {
    if (podPlayShowsTurnText(p)) set.add(p.sentenceIdx - 1);
  }
  return Array.from(set).sort((a, b) => a - b);
}
function podLapDisplayRange(totalSentences2, playedIndices) {
  if (totalSentences2 <= 0 || playedIndices.length === 0) return null;
  return {
    start: Math.max(0, Math.min(...playedIndices)),
    end: Math.min(totalSentences2 - 1, Math.max(...playedIndices))
  };
}

// src/pods/podCohorts.ts
var POD_COLD_START_MIN_SENTENCES = 2;
var sameScene = (a, b) => a == null || b == null || a === b;
function buildPodExchangeCohorts(rows) {
  const sceneRuns = [];
  let start = 0;
  let scene = null;
  for (let i = 0; i < rows.length; i++) {
    const sc = rows[i].scene_number ?? null;
    if (i > start && !sameScene(scene, sc)) {
      sceneRuns.push({ start, end: i });
      start = i;
    }
    if (sc != null) scene = sc;
    else if (i === start) scene = null;
  }
  if (rows.length > start) sceneRuns.push({ start, end: rows.length });
  const cohorts = [];
  for (const run of sceneRuns) {
    const turnStarts = [];
    let turnStart = run.start;
    for (let i = run.start; i < run.end; i++) {
      if (!rows[i].glue_to_next || i === run.end - 1) {
        turnStarts.push(turnStart);
        turnStart = i + 1;
      }
    }
    for (let t = 0; t < turnStarts.length; t += 2) {
      const exStart = turnStarts[t];
      const exEnd = t + 2 < turnStarts.length ? turnStarts[t + 2] : run.end;
      cohorts.push({ start: exStart, size: exEnd - exStart });
    }
  }
  return cohorts;
}
function applyPodColdStartWindow(cohorts) {
  let size = 0;
  let n = 0;
  while (n < cohorts.length && size < POD_COLD_START_MIN_SENTENCES) {
    size += cohorts[n].size;
    n++;
  }
  return n > 1 ? [{ start: 0, size }, ...cohorts.slice(n)] : [...cohorts];
}
function buildPodSentenceCohorts(rows) {
  return rows.map((_, i) => ({ start: i, size: 1 }));
}
function buildPodCohorts(rows) {
  return applyPodColdStartWindow(buildPodSentenceCohorts(rows));
}
function podCohortOrdinalForIndex(cohorts, idx) {
  return cohorts.findIndex((c) => idx >= c.start && idx < c.start + c.size);
}
var totalSentences = (cohorts) => {
  const last = cohorts[cohorts.length - 1];
  return last ? last.start + last.size : 0;
};
function podCohortRoundFor(cohorts, stored) {
  const started = cohorts.filter((c) => c.start < stored).length;
  return started + 1 + Math.max(0, stored - totalSentences(cohorts));
}
function podRatchetAfterLap(cohorts, stored) {
  if (cohorts.length === 0) return stored + 1;
  const started = cohorts.filter((c) => c.start < stored).length;
  const completing = cohorts[Math.min(started, cohorts.length - 1)];
  return Math.max(completing.start + completing.size, stored + 1);
}

export { FUSION_TIER_PREFIX, POD_COLD_START_MIN_SENTENCES, POD_KNOWN_FEMALE_VOICE_FAMILY, POD_KNOWN_VOICE_FAMILY, ROLE_SPEED, SENTENCE_PUNCT, SKIP_FINEST_RUNG, applyPodColdStartWindow, buildFusionGroups, buildFusionRungPlays, buildMainStage, buildPodCohorts, buildPodExchangeCohorts, buildPodSentenceCohorts, computeTurnSpans, dominantVoiceFamily, fineKnownKey, fuseSpans, glueLeadingInterjection, groupDepth, isTargetRole, knownClipsForText, lookupKeyVariants, normalizeForAudio, normalizeForLookup, otherPodVoice, partitionUnits, podCohortOrdinalForIndex, podCohortRoundFor, podLapDisplayRange, podLapPlayedSentenceIndices, podPlayShowsTurnText, podRatchetAfterLap, resolveKnownWithFallback, rungStepsForGroup, spanLadder, turnSpanForIndex, voiceFamily };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map